import {Container} from 'pixi.js';
import {Store, Signal} from '@gongxigames/framework/core/fragments/store';

type EventNames<S extends string> = `${S}->${S}`|`*->${S}`|`${S}->*`|'*->*';

export type StateContainerTransitionHandler<S extends string, D> =
    (self: StateContainer<S, D>, from: S, to: S) => any;

export type StateContainerStatesConfig<S extends string> = S[] | Record<S, S[]>;
export type StateContainerHooks<S extends string, D> = {[KEY in EventNames<S>]?: StateContainerTransitionHandler<S, D>};

export class StateContainer<S extends string = string, D = any> extends Container {
    readonly state: Signal<S>;

    private abortController = new AbortController();
    protected abortSignal = this.abortController.signal;

    constructor(
        readonly initState: S,
        readonly states: StateContainerStatesConfig<S>,
        readonly data?: D,
        transitions?: StateContainerHooks<S, D>,
    ) {
        super();

        this.state = Store.signal('*') as Signal<S>;

        if (transitions) {
            this.onTransitions(transitions);
        }

        this.setState(initState);
    }

    public setState(to: S): Promise<any> {
        this.updateAbortController();

        const from = this.state.peek();
        const promise = new Promise(res => this.once(`${to}->*`, res));

        this.validateTransition(from, to);

        this.state.value = to;

        this.emit('*->*', this, from, to);
        this.emit(`*->${to}`, this, from, to);
        this.emit(`${from}->*`, this, from, to);
        this.emit(`${from}->${to}`, this, from, to);

        return promise;
    }

    async setStateIfPossible(to: S): Promise<any> {
        const currentState = this.state.peek();

        if (this.isTransitionExists(currentState, to)) {
            return this.setState(to);
        }
    }

    public onTransition(transition: EventNames<S>, hook: StateContainerTransitionHandler<S, D>): () => void {
        const [from, to] = transition.split('->') as S[];
        const off = () => this.offTransition(transition, hook);

        this.validateTransition(from, to);
        this.on(transition, hook);

        return off;
    }

    public offTransition(transition: EventNames<S>, hook: StateContainerTransitionHandler<S, D>): void {
        this.off(transition, hook);
    }

    public onTransitions(hooks: StateContainerHooks<S, D>): void {
        for (const transition in hooks) {
            if (Object.prototype.hasOwnProperty.call(hooks, transition)) {
                this.onTransition(transition as any, (hooks as any)[transition]);
            }
        }
    }

    public isTransitionExists(from: S, to: S): boolean {
        const {states} = this;
        const isArray = Array.isArray(states);

        if (isArray) {
            const isFromOk = from === '*' ? true : states.includes(from);
            const isToOk = to === '*' ? true : states.includes(to);

            return isFromOk && isToOk;
        } else {
            const isFromStar = from === '*';
            const isToStar = to === '*';

            if (isFromStar && isToStar) {
                return true;
            } else if (isFromStar && !isToStar){
                return !!states[to];
            } else if (!isFromStar && isToStar) {
                return !!states[from];
            } else {
                return !!states[from] && states[from].includes(to);
            }
        }
    }

    protected validateTransition(from: S, to: S): void {
        if (!this.isTransitionExists(from, to)){
            throw new Error(`StateContainer: Unavailable transition ${from}->${to}`);
        }
    }

    protected updateAbortController(): void {
        this.abortController.abort();
        this.abortController = new AbortController();
        this.abortSignal = this.abortController.signal;
    }
}